<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import documentReviewImage from 'dashboard/assets/real-estate/nucleo-work-order-execution/dialog-document-review.png';
import serviceCompletionImage from 'dashboard/assets/real-estate/nucleo-work-order-execution/dialog-service-completion.png';
import orderCompletionImage from 'dashboard/assets/real-estate/nucleo-work-order-execution/dialog-order-completion.png';

const props = defineProps({
  show: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  variant: {
    type: String,
    default: 'update',
    validator: value => ['update', 'complete', 'finalize'].includes(value),
  },
});
const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const content = computed(
  () =>
    ({
      update: {
        image: documentReviewImage,
        title: t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_TITLE.UPDATE'),
        description: t(
          'NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_COPY.UPDATE'
        ),
        action: t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM.UPDATE'),
      },
      complete: {
        image: serviceCompletionImage,
        title: t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_TITLE.COMPLETE'),
        description: t(
          'NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_COPY.COMPLETE'
        ),
        action: t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM.COMPLETE'),
      },
      finalize: {
        image: orderCompletionImage,
        title: t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_TITLE.FINALIZE'),
        description: t(
          'NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_COPY.FINALIZE'
        ),
        action: t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM.FINALIZE'),
      },
    })[props.variant]
);
</script>

<template>
  <LaisModal
    :show="show"
    disable-escape-key-down
    full-screen
    full-screen-layout-class="relative h-[100dvh] max-h-none rounded-none lg:h-auto lg:max-h-[calc(100dvh-2rem)] lg:rounded-lais-xl"
    full-screen-max-width-class="lg:max-w-[960px]"
    header-class="absolute right-2 top-2 z-10 flex p-0"
    @close="!saving && emit('close')"
  >
    <template #header>
      <span class="sr-only">{{ content.title }}</span>
    </template>
    <section
      data-testid="nucleo-work-order-confirmation-dialog"
      class="grid gap-0 lg:grid-cols-[minmax(0,412px)_minmax(0,1fr)] lg:items-stretch"
    >
      <div class="flex justify-center lg:justify-start">
        <img
          :src="content.image"
          :alt="content.title"
          class="h-auto max-h-[533px] w-auto max-w-full object-contain"
        />
      </div>
      <div
        data-testid="nucleo-work-order-confirmation-content"
        class="flex min-w-0 flex-col justify-between px-5 py-5 lg:min-h-[533px] lg:pb-[50px] lg:pl-[90px] lg:pr-[25px] lg:pt-[50px]"
      >
        <div>
          <h3
            class="font-redHatDisplay text-[24px] font-bold leading-[30px] text-lais-text lg:w-[317px] lg:text-[40px] lg:leading-[42px]"
          >
            {{ content.title }}
          </h3>
          <div
            class="mt-[25px] space-y-[25px] text-base leading-6 text-lais-text"
          >
            <p>{{ content.description }}</p>
            <p v-if="variant === 'finalize'">
              {{ t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.CONFIRM_NOTE') }}
            </p>
          </div>
        </div>
        <div
          data-testid="nucleo-work-order-confirmation-action"
          class="mt-[30px] flex w-full justify-end lg:mt-0"
        >
          <LaisPrimaryButton
            size="md"
            :is-loading="saving"
            @click="emit('confirm')"
          >
            {{ content.action }}
          </LaisPrimaryButton>
        </div>
      </div>
    </section>
  </LaisModal>
</template>
